'use client';

import { Fragment, useState } from 'react';
import Image from 'next/image';
import SocialLinks from './SocialLinks';
import Teamdata from '../textContent/team/team.json';
import { motion } from 'framer-motion';

const Card = () => {
    const [hoveredCard, setHoveredCard] = useState(null);

    /* Animations */
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1, delayChildren: 0.2 },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.5, ease: 'easeOut' },
        },
    };

    return (
        <Fragment>
            <div className="px-0 py-10 md:py-14 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 px-4"
                >
                    <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-gray-800">
                        Our Leadership Team
                    </h1>

                    <p className="mt-4 text-sm sm:text-base md:text-lg text-gray-700 font-semibold max-w-3xl mx-auto">
                        This is our team, a lot of smiling happy people who work
                        hard to empower your teams.
                    </p>

                    <div className="flex justify-center mt-6">
                        <div className="w-16 h-1 bg-pink-400 rounded-full" />
                    </div>
                </motion.div>

                {/* Cards */}
                <motion.div
                    className="max-w-12xl mx-auto px-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-5"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    {Teamdata.map((member, index) => (
                        <motion.div
                            key={index}
                            variants={cardVariants}
                            whileHover={{ y: -6, scale: 1.02 }}
                            onMouseEnter={() => setHoveredCard(member.name)}
                            onMouseLeave={() => setHoveredCard(null)}
                            className="bg-white rounded-2xl shadow-md overflow-hidden border flex flex-col"
                        >
                            {/* Image */}
                            <div className="relative w-full h-80 sm:h-96 lg:h-72 xl:h-80 bg-white lg:bg-gradient-to-br lg:from-blue-100 lg:to-purple-100 p-3">
                                <Image
                                    src={
                                        hoveredCard === member.name &&
                                        member.cameoImage
                                            ? member.cameoImage
                                            : member.photo
                                    }
                                    alt={member.name}
                                    fill
                                    sizes="(max-width:640px) 100vw, (max-width:768px) 50vw, (max-width:1024px) 33vw, (max-width:1280px) 25vw, 20vw"
                                    className="object-contain sm:object-cover"
                                    priority={index < 5}
                                />
                            </div>

                            {/* Content – CENTERED */}
                            <div className="flex-1 flex flex-col items-center justify-center text-center p-5 lg:p-4">
                                <h2 className="text-base lg:text-lg font-bold text-gray-800">
                                    {member.name}
                                </h2>

                                <p className="text-sm text-gray-600 font-medium mt-1">
                                    {member.position}
                                </p>

                                <div className="mt-4">
                                    <SocialLinks links={member.links} />
                                </div>
                            </div>

                            {/* Mobile accent line */}
                            <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 block lg:hidden" />
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </Fragment>
    );
};

export default Card;
